import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Slider
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Business as OrganizationIcon,
  Business,
  TrendingUp as UtilizationIcon,
  LocalHospital,
  PieChart,
  Route,
  Edit as EditIcon,
  Schedule,
  LocationOn,
  HealthAndSafety,
  MedicalServices,
  Payment,
  Visibility,
  Close as CloseIcon
} from '@mui/icons-material';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import './ThreeDMap.css';

const MarketIntelligenceTray: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showFilters, setShowFilters] = useState(true);

  // State for all filters (matching MarketIntelligence.tsx)
  const [encounterTypes, setEncounterTypes] = useState<string[]>([]);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<string[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [selectedHcpcsCodes, setSelectedHcpcsCodes] = useState<string[]>([]);
  const [timePeriod, setTimePeriod] = useState('6months');
  const [customDateRange, setCustomDateRange] = useState({ start: new Date(), end: new Date() });
  const [geographyType, setGeographyType] = useState('zip');
  const [geographyValue, setGeographyValue] = useState('');
  const [showShortageAreas, setShowShortageAreas] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedPayors, setSelectedPayors] = useState<string[]>([]);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [travelTime, setTravelTime] = useState<number>(15);
  const [encounterGroup, setEncounterGroup] = useState<string>('unique');

  // Event handlers (matching MarketIntelligence.tsx)
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEncounterTypesChange = (event: any) => {
    setEncounterTypes(event.target.value as string[]);
  };

  const handleIcdCodesChange = (event: any) => {
    setSelectedIcdCodes(event.target.value as string[]);
  };

  const handleCptCodesChange = (event: any) => {
    setSelectedCptCodes(event.target.value as string[]);
  };

  const handleHcpcsCodesChange = (event: any) => {
    setSelectedHcpcsCodes(event.target.value as string[]);
  };

  const handleTimePeriodChange = (event: any) => {
    setTimePeriod(event.target.value);
  };

  const handleCustomDateChange = (part: 'start' | 'end', dateStr: string) => {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      setCustomDateRange(prev => ({ ...prev, [part]: date }));
    }
  };

  const handleGeographyTypeChange = (event: any) => {
    setGeographyType(event.target.value);
  };

  const handleDrawBoundary = () => {
    // Implementation of drawing boundary
  };

  const handleTravelTimeChange = (event: any) => {
    setTravelTime(event.target.value as number);
  };

  const handleShortageAreasChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowShortageAreas(event.target.checked);
  };

  const handleSpecialtiesChange = (event: any) => {
    setSelectedSpecialties(event.target.value as string[]);
  };

  const handleOrganizationsChange = (event: any) => {
    setSelectedOrganizations(event.target.value as string[]);
  };

  const handlePayorsChange = (event: any) => {
    setSelectedPayors(event.target.value as string[]);
  };

  const handleHeatmapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHeatmap(event.target.checked);
  };

  const handlePointsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowPoints(event.target.checked);
  };

  const handleEncounterGroupChange = (event: any) => {
    setEncounterGroup(event.target.value);
  };

  const renderCompetitorFilters = () => (
    <Stack spacing={2}>
      {/* Organization Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrganizationIcon fontSize="small" color="primary" />
            Organization
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Healthcare Organization Name"
              placeholder="Search organizations..."
            />
            <TextField
              fullWidth
              size="small"
              label="Parent Organization Name"
              placeholder="Search parent organizations..."
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Specialty & Service Lines Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital fontSize="small" color="primary" />
            Specialties & Services
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Specialty/Subspecialty</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="cardiology">Cardiology</MenuItem>
                <MenuItem value="orthopedics">Orthopedics</MenuItem>
                <MenuItem value="neurology">Neurology</MenuItem>
                <MenuItem value="oncology">Oncology</MenuItem>
                <MenuItem value="pediatrics">Pediatrics</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Service Lines</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="primary">Primary Care</MenuItem>
                <MenuItem value="emergency">Emergency Medicine</MenuItem>
                <MenuItem value="surgery">Surgery</MenuItem>
                <MenuItem value="diagnostic">Diagnostic Services</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Patient Volume Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UtilizationIcon fontSize="small" color="primary" />
            Patient Volume
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select value="1year" onChange={() => {}}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">Volume Range</Typography>
              <Slider
                value={[0, 1000]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={10000}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Market Share Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PieChart fontSize="small" color="primary" />
            Market Share
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter By</InputLabel>
              <Select value="service" onChange={() => {}}>
                <MenuItem value="service">Service Line</MenuItem>
                <MenuItem value="specialty">Specialty</MenuItem>
                <MenuItem value="subspecialty">Subspecialty</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">Market Share Range (%)</Typography>
              <Slider
                value={[0, 100]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={100}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Shared Patient Journeys Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Route fontSize="small" color="primary" />
            Shared Patient Journeys
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Referral Source */}
            <FormControl fullWidth size="small">
              <InputLabel>Referral Source</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="inpatient">Inpatient</MenuItem>
                <MenuItem value="outpatient">Outpatient</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Specialty/Service Line */}
            <FormControl fullWidth size="small">
              <InputLabel>Specialty/Service Line</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="cardiology">Cardiology</MenuItem>
                <MenuItem value="orthopedics">Orthopedics</MenuItem>
                <MenuItem value="primary">Primary Care</MenuItem>
              </Select>
            </FormControl>

            {/* Referring Provider */}
            <TextField
              fullWidth
              size="small"
              label="Referring Provider"
              placeholder="Search providers..."
            />

            {/* Geographic Boundaries */}
            <FormControl fullWidth size="small">
              <InputLabel>Geographic Boundary</InputLabel>
              <Select value="county" onChange={() => {}}>
                <MenuItem value="county">County</MenuItem>
                <MenuItem value="zip">ZIP Code</MenuItem>
                <MenuItem value="census">Census Tract</MenuItem>
                <MenuItem value="block">Block Group</MenuItem>
                <MenuItem value="trade">Trade Area</MenuItem>
                <MenuItem value="custom">Custom (Draw on Map)</MenuItem>
              </Select>
            </FormControl>

            {/* Time Range */}
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select value="1year" onChange={() => {}}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Revenue Range */}
            <Box>
              <Typography variant="caption" color="text.secondary">Revenue Range ($)</Typography>
              <Slider
                value={[0, 1000000]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={10000000}
                valueLabelFormat={(value) => `$${value.toLocaleString()}`}
              />
            </Box>

            {/* Journey Visualization Controls */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Journey Visualization</Typography>
              <FormControlLabel
                control={<Switch size="small" />}
                label="Show Out-Migration Points"
              />
              <FormControlLabel
                control={<Switch size="small" />}
                label="Show Predictive Pathways"
              />
            </Box>

            {/* Prediction Options */}
            <FormControl fullWidth size="small">
              <InputLabel>Prediction Range</InputLabel>
              <Select value="6month" onChange={() => {}}>
                <MenuItem value="6month">6 Month Prediction</MenuItem>
                <MenuItem value="1year">1 Year Prediction</MenuItem>
                <MenuItem value="5year">5 Year Prediction</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  const renderUtilizationFilters = () => (
    <Stack spacing={2}>
      {/* Encounter Group */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <FilterIcon fontSize="small" color="primary" />
            Encounter Group
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Encounter Type</InputLabel>
            <Select value={encounterGroup} onChange={handleEncounterGroupChange}>
              <MenuItem value="unique">Unique Encounters</MenuItem>
              <MenuItem value="total">Total Encounters</MenuItem>
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Type */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <LocalHospital fontSize="small" color="primary" />
            Encounters by Type
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Encounter Type</InputLabel>
              <Select multiple value={encounterTypes} onChange={handleEncounterTypesChange}>
                <MenuItem value="inpatient">Inpatient</MenuItem>
                <MenuItem value="outpatient">Outpatient</MenuItem>
              </Select>
            </FormControl>
            
            {/* ICD Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>ICD-10 Codes</InputLabel>
              <Select multiple value={selectedIcdCodes} onChange={handleIcdCodesChange}>
                {/* Populate with actual ICD codes from data */}
              </Select>
            </FormControl>

            {/* CPT Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>CPT Codes</InputLabel>
              <Select multiple value={selectedCptCodes} onChange={handleCptCodesChange}>
                {/* Populate with actual CPT codes from data */}
              </Select>
            </FormControl>

            {/* HCPCS Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>HCPCS Codes</InputLabel>
              <Select multiple value={selectedHcpcsCodes} onChange={handleHcpcsCodesChange}>
                {/* Populate with actual HCPCS codes from data */}
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Timeframe */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Schedule fontSize="small" color="primary" />
            Encounters by Timeframe
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select value={timePeriod} onChange={handleTimePeriodChange}>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="12months">Last 12 Months</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
            
            {timePeriod === 'custom' && (
              <Stack spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={customDateRange.start.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('start', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={customDateRange.end.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('end', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Geography */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <LocationOn fontSize="small" color="primary" />
            Encounters by Geography
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Geographic Boundary</InputLabel>
              <Select value={geographyType} onChange={handleGeographyTypeChange}>
                <MenuItem value="zip">ZIP Code</MenuItem>
                <MenuItem value="county">County</MenuItem>
                <MenuItem value="city">City</MenuItem>
                <MenuItem value="state">State</MenuItem>
                <MenuItem value="census">Census Tract</MenuItem>
                <MenuItem value="block">Block Group</MenuItem>
                <MenuItem value="custom">Custom Boundary</MenuItem>
                <MenuItem value="trade">Trade Area</MenuItem>
              </Select>
            </FormControl>

            {geographyType === 'custom' && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleDrawBoundary}
                startIcon={<EditIcon />}
              >
                Draw Boundary
              </Button>
            )}

            {geographyType === 'trade' && (
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="Point of Interest"
                  placeholder="Search location..."
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Travel Time</InputLabel>
                  <Select value={travelTime} onChange={handleTravelTimeChange}>
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>60 minutes</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Healthcare Shortage Areas */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <HealthAndSafety fontSize="small" color="primary" />
            Healthcare Shortage Areas
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControlLabel
            control={<Switch checked={showShortageAreas} onChange={handleShortageAreasChange} />}
            label={<Typography variant="body2">Show Healthcare Shortage Areas</Typography>}
          />
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Specialty */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <MedicalServices fontSize="small" color="primary" />
            Encounters by Specialty
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Specialties</InputLabel>
            <Select multiple value={selectedSpecialties} onChange={handleSpecialtiesChange}>
              {/* Populate with actual specialties from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Healthcare Organization */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Business fontSize="small" color="primary" />
            Encounters by Organization
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Organizations</InputLabel>
            <Select multiple value={selectedOrganizations} onChange={handleOrganizationsChange}>
              {/* Populate with actual organizations from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Payor */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Payment fontSize="small" color="primary" />
            Encounters by Payor
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Payors</InputLabel>
            <Select multiple value={selectedPayors} onChange={handlePayorsChange}>
              {/* Populate with actual payors from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Visualization Options */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Visibility fontSize="small" color="primary" />
            Visualization Options
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={showHeatmap} onChange={handleHeatmapChange} />}
              label={<Typography variant="body2">Show Heatmap</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={showPoints} onChange={handlePointsChange} />}
              label={<Typography variant="body2">Show Individual Points</Typography>}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  return (
    <Box className="market-intelligence-tray adaptive-scenario-tray">
      <Box className="market-intelligence-tray-header">
        <Typography variant="h6">Market Intelligence</Typography>
        <IconButton size="small" onClick={onClose} className="market-intelligence-close-btn">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="market-intelligence-tray-controls">
        <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
        <Button variant="contained" size="small" sx={{ ml: 1 }} startIcon={<AddIcon />}>New Analysis</Button>
      </Box>
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)} 
        aria-label="market intelligence tabs" 
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          px: 2, 
          pt: 1,
          '& .MuiTab-root': {
            minWidth: 'auto',
            fontSize: '0.875rem'
          }
        }}
      >
        <Tab icon={<OrganizationIcon sx={{ mr: 1 }} />} iconPosition="start" label="Competitors & Third-Party Healthcare Organizations" />
        <Tab icon={<UtilizationIcon sx={{ mr: 1 }} />} iconPosition="start" label="Utilization and Demand" />
      </Tabs>
      <Box className="market-intelligence-tray-content">
        {showFilters && (
          <Box className="market-intelligence-filters">
            {activeTab === 0 ? renderCompetitorFilters() : renderUtilizationFilters()}
          </Box>
        )}
        {/* You can add a map or other content here if needed */}
      </Box>
    </Box>
  );
};

export default MarketIntelligenceTray;
